/**
 * TechnicalDetails (SYN-1486) — the ONLY place raw technical identifiers may
 * render on a buyer surface. A collapsed-by-default expander holding
 * capability IDs, contract/listing ids, trace refs, evidence sources, etc.
 * for admins/support: hidden, not deleted (SYN-1485 constraint).
 *
 * Marked `data-technical-details` so the vocabulary-leakage test can exclude
 * its contents when scanning buyer-visible text.
 */

import type { ReactNode } from "react";
import { TECHNICAL_DETAILS_LABEL } from "../lib/buyer-language";
import { cn } from "../lib/utils";

export interface TechnicalDetailsEntry {
  label: string;
  value: ReactNode;
}

export function TechnicalDetails({
  entries,
  className,
}: {
  entries: TechnicalDetailsEntry[];
  className?: string;
}) {
  const visible = entries.filter((entry) => entry.value !== null && entry.value !== undefined);
  if (visible.length === 0) {
    return null;
  }
  return (
    <details
      data-technical-details
      className={cn("rounded-md border border-dashed px-3 py-2", className)}
    >
      <summary className="cursor-pointer select-none text-xs font-medium text-muted-foreground">
        {TECHNICAL_DETAILS_LABEL}
      </summary>
      <dl className="mt-2 space-y-1">
        {visible.map((entry, i) => (
          <div key={i} className="flex items-baseline gap-2 text-[11px]">
            <dt className="shrink-0 text-muted-foreground">{entry.label}</dt>
            <dd className="min-w-0 break-all font-mono text-foreground/80">{entry.value}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}
